// Transient notifications ("Saved", "Couldn't save: you don't have permission", "Copied"). The shell mounts one
// ToastProvider; screens call useToast().success/error/info. Errors stay longer and are announced assertively
// (role=alert); others are polite. Timers pause while the pointer or focus is on the stack. The stack is a
// top-layer popover, raised on every new toast, so it shows above an open modal dialog.

import { CircleAlert, CircleCheck, Info, X } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { cn } from './cn';
import { raisePopover, showPopoverSafe } from './position';

export type ToastTone = 'success' | 'error' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  /** Bold first line; the message then reads as the detail. */
  title?: ReactNode;
  /** Milliseconds before it hides; 0 keeps it until dismissed. Defaults: 4 s (success/info), 7 s (error). */
  duration?: number;
  action?: ToastAction;
  /** Replaces an existing toast with the same id instead of stacking (e.g. repeated "Offline"). */
  id?: string;
}

export interface ToastApi {
  show: (tone: ToastTone, message: ReactNode, options?: ToastOptions) => string;
  success: (message: ReactNode, options?: ToastOptions) => string;
  error: (message: ReactNode, options?: ToastOptions) => string;
  info: (message: ReactNode, options?: ToastOptions) => string;
  /** Dismiss one toast, or all when no id is given. */
  dismiss: (id?: string) => void;
}

interface ToastItem {
  id: string;
  tone: ToastTone;
  message: ReactNode;
  title?: ReactNode;
  duration: number;
  action?: ToastAction;
  /** Bumps when a toast with the same id is replaced, to restart its timer. */
  version: number;
}

const DEFAULT_DURATION: Record<ToastTone, number> = { success: 4000, info: 4000, error: 7000 };
const MAX_VISIBLE = 4;

const ToastContext = createContext<ToastApi | null>(null);

let warned = false;
const fallbackApi: ToastApi = (() => {
  const warn = (message: ReactNode) => {
    if (!warned && import.meta.env.DEV) {
      warned = true;
      console.warn('useToast() outside <ToastProvider>; toast dropped:', message);
    }
    return '';
  };
  return {
    show: (_tone, message) => warn(message),
    success: (message) => warn(message),
    error: (message) => warn(message),
    info: (message) => warn(message),
    dismiss: () => {},
  };
})();

/** The toast API. Outside a ToastProvider (signed-out pages) it is a no-op, so shared code may call it freely. */
export function useToast(): ToastApi {
  return useContext(ToastContext) ?? fallbackApi;
}

let counter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id?: string) => {
    setToasts((list) => (id === undefined ? [] : list.filter((t) => t.id !== id)));
  }, []);

  const show = useCallback((tone: ToastTone, message: ReactNode, options: ToastOptions = {}) => {
    counter += 1;
    const id = options.id ?? `toast-${counter}`;
    const item: ToastItem = {
      id,
      tone,
      message,
      title: options.title,
      duration: options.duration ?? DEFAULT_DURATION[tone],
      action: options.action,
      version: counter,
    };
    setToasts((list) => {
      const existing = list.findIndex((t) => t.id === id);
      if (existing >= 0) {
        const next = list.slice();
        next[existing] = item;
        return next;
      }
      // Oldest out first when the stack is full.
      return [...list, item].slice(-MAX_VISIBLE);
    });
    return id;
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (m, o) => show('success', m, o),
      error: (m, o) => show('error', m, o),
      info: (m, o) => show('info', m, o),
      dismiss,
    }),
    [show, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

function ToastViewport({ toasts, onDismiss }: { toasts: ToastItem[]; onDismiss: (id: string) => void }) {
  const ref = useRef<HTMLElement>(null);
  const [paused, setPaused] = useState(false);
  const newest = toasts.length ? toasts[toasts.length - 1]!.version : 0;

  // Keep the (empty) region in the top layer so the live region exists before messages arrive.
  useLayoutEffect(() => {
    showPopoverSafe(ref.current);
  }, []);
  // Re-raise above dialogs opened since the last toast.
  useLayoutEffect(() => {
    if (newest) raisePopover(ref.current);
  }, [newest]);

  return (
    <section
      ref={ref}
      aria-label="Notifications"
      aria-live="polite"
      aria-relevant="additions"
      popover="manual"
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setPaused(false);
      }}
      className={cn(
        'pointer-events-none fixed m-0 flex w-[min(380px,calc(100vw-24px))] flex-col gap-2 overflow-visible',
        // Explicit sides (the UA popover style is inset: 0; margin: auto).
        'top-auto left-auto right-3 bottom-3 border-0 bg-transparent p-0 sm:right-5 sm:bottom-5',
        'z-[var(--gt-z-toast)]',
      )}
    >
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} paused={paused} onDismiss={onDismiss} />
      ))}
    </section>
  );
}

const TONE_ICON = {
  success: CircleCheck,
  error: CircleAlert,
  info: Info,
} as const;

const TONE_ICON_CLASS: Record<ToastTone, string> = {
  success: 'text-success',
  error: 'text-danger',
  info: 'text-info',
};

function ToastCard({ toast, paused, onDismiss }: { toast: ToastItem; paused: boolean; onDismiss: (id: string) => void }) {
  const remaining = useRef(toast.duration);
  const started = useRef(0);

  // Restart the clock when the toast is replaced (same id, new version).
  useEffect(() => {
    remaining.current = toast.duration;
  }, [toast.version, toast.duration]);

  useEffect(() => {
    if (toast.duration <= 0 || paused) return;
    started.current = Date.now();
    const handle = window.setTimeout(() => onDismiss(toast.id), Math.max(0, remaining.current));
    return () => {
      window.clearTimeout(handle);
      remaining.current -= Date.now() - started.current;
    };
  }, [paused, toast.id, toast.version, toast.duration, onDismiss]);

  const Icon = TONE_ICON[toast.tone];
  return (
    <div
      role={toast.tone === 'error' ? 'alert' : 'status'}
      className={cn(
        'pointer-events-auto flex items-start gap-3 rounded-tile border border-line bg-surface px-4 py-3 shadow-pop',
        'animate-toast-in',
      )}
    >
      <Icon aria-hidden className={cn('mt-0.5 size-5 shrink-0', TONE_ICON_CLASS[toast.tone])} />
      <div className="min-w-0 flex-1 text-sm">
        {toast.title != null && <p className="font-semibold text-fg">{toast.title}</p>}
        <div className={cn(toast.title != null ? 'text-fg-muted' : 'font-medium text-fg', 'break-words')}>
          {toast.message}
        </div>
        {toast.action && (
          <button
            type="button"
            onClick={() => {
              toast.action?.onClick();
              onDismiss(toast.id);
            }}
            className="mt-1.5 text-sm font-semibold text-link hover:underline"
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button
        type="button"
        aria-label="Dismiss notification"
        onClick={() => onDismiss(toast.id)}
        className="-mt-0.5 -mr-1.5 flex size-7 shrink-0 items-center justify-center rounded-full text-fg-subtle hover:bg-surface-3 hover:text-fg"
      >
        <X aria-hidden className="size-4" />
      </button>
    </div>
  );
}
